import type { EmailRecipient, Fryer, FryerTypeRules, StoreConfig } from "./types";

export const DEFAULT_STORE_CODE = "CFA00000";

export const DEFAULT_TYPE_RULES: FryerTypeRules = {
  "Pressure Fryer": { neededDays: 22, overdueDays: 28 },
  "Open Single Fryer": { neededDays: 22, overdueDays: 28 },
  "Open Double Fryer": { neededDays: 22, overdueDays: 28 }
};

export const DEFAULT_CONFIG: StoreConfig = {
  usersForEmailAlerts: [],
  typeRules: DEFAULT_TYPE_RULES,
  alertSettings: { overdueReminderDays: 0 },
  storeStatus: { isLive: false },
  featureFlags: { emailAlertsLive: false, testingMode: false }
};

function finiteNumber(value: unknown, fallback: number): number {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
}

export function normalizeStoreCode(value: string | null | undefined): string {
  const normalized = String(value ?? "").trim().toUpperCase();
  return normalized || DEFAULT_STORE_CODE;
}

export function normalizeRecipient(value: unknown): EmailRecipient | null {
  if (typeof value === "string") {
    const email = value.trim().toLowerCase();
    if (!email) return null;
    return {
      name: "",
      email,
      alertsEnabled: true,
      assignmentAlerts: true,
      overdueAlerts: true
    };
  }

  if (!value || typeof value !== "object") return null;
  const raw = value as Partial<EmailRecipient>;
  const email = String(raw.email ?? "").trim().toLowerCase();
  if (!email) return null;

  return {
    name: String(raw.name ?? "").trim(),
    email,
    alertsEnabled: raw.alertsEnabled !== false,
    assignmentAlerts: raw.assignmentAlerts !== false,
    overdueAlerts: raw.overdueAlerts !== false
  };
}

export function normalizeConfig(value: unknown): StoreConfig {
  const raw = value && typeof value === "object" ? value as Record<string, unknown> : {};
  const rawAlertSettings = raw.alertSettings && typeof raw.alertSettings === "object"
    ? raw.alertSettings as Record<string, unknown>
    : {};
  const rawStoreStatus = raw.storeStatus && typeof raw.storeStatus === "object"
    ? raw.storeStatus as Record<string, unknown>
    : {};
  const rawFeatureFlags = raw.featureFlags && typeof raw.featureFlags === "object"
    ? raw.featureFlags as Record<string, unknown>
    : {};
  const recipients = Array.isArray(raw.usersForEmailAlerts)
    ? raw.usersForEmailAlerts.map(normalizeRecipient).filter((item): item is EmailRecipient => Boolean(item))
    : [];

  return {
    usersForEmailAlerts: recipients,
    typeRules: raw.typeRules && typeof raw.typeRules === "object"
      ? raw.typeRules as FryerTypeRules
      : DEFAULT_TYPE_RULES,
    alertSettings: {
      overdueReminderDays: Math.max(0, finiteNumber(rawAlertSettings.overdueReminderDays, 0))
    },
    storeStatus: {
      isLive: rawStoreStatus.isLive === true
    },
    featureFlags: {
      emailAlertsLive: rawFeatureFlags.emailAlertsLive === true,
      testingMode: rawFeatureFlags.testingMode === true
    },
    ...(typeof raw.stripeSubscriptionId === "string" ? { stripeSubscriptionId: raw.stripeSubscriptionId } : {}),
    ...(typeof raw.stripeCustomerId === "string" ? { stripeCustomerId: raw.stripeCustomerId } : {})
  };
}

export function normalizeFryer(id: string, value: unknown): Fryer {
  const raw = value && typeof value === "object" ? value as Record<string, unknown> : {};
  return {
    id,
    name: String(raw.name ?? `Fryer ${id}`),
    type: String(raw.type ?? "Pressure Fryer"),
    lastBoilOut: String(raw.lastBoilOut ?? ""),
    needsBoilOut: raw.needsBoilOut === true,
    needsReason: String(raw.needsReason ?? ""),
    needsNotes: String(raw.needsNotes ?? ""),
    history: Array.isArray(raw.history) ? raw.history as Fryer["history"] : [],
    ...(raw.emailAlertState && typeof raw.emailAlertState === "object"
      ? { emailAlertState: raw.emailAlertState as Fryer["emailAlertState"] }
      : {})
  };
}

export function storeAllowsAccess(config: StoreConfig): boolean {
  return config.storeStatus.isLive || config.featureFlags.testingMode;
}
